/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useEffect, useState
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    Brain, Cpu
} from 'lucide-react';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    providerParameterDefaults
} from 'builders/builderInternal/components/Common/providerConfigRegistry';
import {
    InstructionFormDialog
} from 'builders/destudio/opcode-editor/inline-pickers/InstructionPicker';
import DSResourceList, { PAGE_SIZE_LIST } from './DSResourceList';
import {
    getInstructionTimestamp, sortInstructionsNewestFirst
} from './instructionListUtils';

/**
 * DSInstructionsList — DS-themed list for Library → Instructions.
 *
 * Create/Edit happen INSIDE the list via the same DS-themed dialog used by
 * the inline opcode picker (model catalog + provider params + popup prompt
 * editor). No more bouncing to the legacy `/ai-lab/instructions` hub.
 */
export default function DSInstructionsList() {
    const navigate = useNavigate();
    const {
        oragID, subOragID
    } = useParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    // When set, we fetch the full set and let DSResourceList filter across all
    // pages (the list API has no search param, so search can't be server-side).
    const [search, setSearch] = useState('');

    // Form state — used for both Create + Edit
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('new');
    const [formInitial, setFormInitial] = useState(null);

    function fetchPage(page) {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        const searching = search.length > 0;
        instructionService.listModels(oragID, subOragID, null, searching ? 1 : page, searching ? 1000 : PAGE_SIZE_LIST)
            .then((res) => {
                const payload = res?.data ?? res;
                if (payload && typeof payload === 'object' && 'data' in payload && 'pagination' in payload) {
                    const valid = (Array.isArray(payload.data) ? payload.data : [])
                        .filter((x) => x && typeof x === 'object' && x.unique_name && x.model_type);
                    setItems(sortInstructionsNewestFirst(valid));
                    setTotalPages(payload.pagination.totalPages ?? 1);
                    setTotalItems(payload.pagination.totalItems ?? valid.length);
                } else {
                    const arr = Array.isArray(payload) ? payload : [];
                    const valid = arr.filter((x) => x && typeof x === 'object' && x.unique_name && x.model_type);
                    setItems(sortInstructionsNewestFirst(valid));
                    setTotalPages(1);
                    setTotalItems(valid.length);
                }
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }

    function reload() { fetchPage(currentPage); }
    useEffect(() => { setCurrentPage(1); }, [oragID, subOragID]);
    useEffect(() => { fetchPage(currentPage); }, [oragID, subOragID, currentPage, search]);

    function openNew() {
        setFormInitial({
            unique_name: '',
            model_type: 'Groq',
            modelId: '',
            prompt: '',
            modelDescription: '',
            parameters: {
                ...(providerParameterDefaults.Groq || {})
            }
        });
        setFormMode('new');
        setFormOpen(true);
    }
    async function openEdit(it) {
        // Try in-memory first; otherwise fetch the full doc
        let full = it;
        if (!full?.prompt) {
            try {
                const r = await instructionService.getModel(oragID, subOragID, it.model_type, it.unique_name);
                full = r?.data || r || it;
            } catch { /* fall back to summary */ }
        }
        setFormInitial(full);
        setFormMode('edit');
        setFormOpen(true);
    }
    async function handleDelete(it) {
        if (!window.confirm(`Delete instruction "${it.unique_name}" (${it.model_type})?`)) return;
        try {
            await instructionService.deleteModels({
                organizationId: oragID,
                subOrganizationId: subOragID,
                items: [{
                    unique_name: it.unique_name,
                    model_type: it.model_type
                }]
            });
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }

    return (
        <>
            <DSResourceList
                items={items}
                loading={loading}
                error={error}
                getKey={(it) => `${it.model_type}:${it.unique_name}`}
                getCard={(it) => ({
                    icon: Brain,
                    tone: 'violet',
                    title: it.unique_name,
                    subtitle: it.modelId || null,
                    description: it.modelDescription || it.prompt?.slice(0, 200) || '',
                    version: it.version ? `v${it.version}` : null,
                    badge: it.model_type
                        ? { tone: 'neutral', label: it.model_type }
                        : undefined,
                    stats: [
                        it.parameters?.temperature !== undefined && {
                            icon: Cpu,
                            label: 'temp',
                            value: it.parameters.temperature
                        }
                    ].filter(Boolean),
                    createdBy: it.createdBy || null,
                    createdAt: getInstructionTimestamp(it),
                    searchTerms: [it.modelId, it.model_type, it.unique_name, it.modelDescription]
                })}
                onPick={openEdit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onCreate={openNew}
                newLabel="New instruction"
                searchPlaceholder="Search instructions…"
                emptyTitle="No instructions yet"
                emptyDescription="Save your first reusable LLM prompt — your assistants pick from here."
                emptyIcon={Brain}
                serverPage={search ? undefined : currentPage}
                serverTotalPages={search ? undefined : totalPages}
                onServerPage={search ? undefined : setCurrentPage}
                serverItemCount={search ? undefined : totalItems}
                onSearchChange={(q) => { setSearch(q); setCurrentPage(1); }}
            />
            {formOpen && (
                <InstructionFormDialog
                    open={formOpen}
                    onOpenChange={setFormOpen}
                    mode={formMode}
                    initial={formInitial}
                    onSaved={() => { setFormOpen(false); reload(); }}
                />
            )}
        </>
    );
}
